import { useState, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { debugLogger } from '@/utils/debugLogger';
import { useLikeLimits } from '@/hooks/useLikeLimits';
import { toast } from 'sonner';
import { blockService } from '@/core/services/BlockService';

export type SimpleConnectionStatus = 'none' | 'sent' | 'received' | 'matched';

interface ConnectionCache {
  [userId: string]: {
    status: SimpleConnectionStatus;
    timestamp: number;
  };
}

const connectionCache: ConnectionCache = {};
const CACHE_TTL = 5 * 60 * 1000; // 5 minutos

export const useSimpleConnectionStatus = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const { likeLimits, incrementLikeCount } = useLikeLimits();

  const getConnectionStatus = useCallback(async (targetUserId: string): Promise<SimpleConnectionStatus> => {
    if (!user || !targetUserId) return 'none';

    // Verificar cache primeiro
    const cached = connectionCache[targetUserId];
    if (cached && (Date.now() - cached.timestamp) < CACHE_TTL) {
      debugLogger.debug('SimpleConnectionStatus: Using cached status for', targetUserId);
      return cached.status;
    }

    try {
      debugLogger.debug('SimpleConnectionStatus: Fetching fresh status for', targetUserId);
      
      // Using RPC for optimized connection status check
      const { data: connections, error } = await (supabase as any)
        .rpc('get_user_connections_status', { target_user_id: targetUserId }) as { data: Array<{ id: string; status: string; user_id: string; target_id: string; initiated_by: string }> | null; error: any };

      if (error) {
        debugLogger.error('Error checking connection status:', error);
        return 'none';
      }

      if (!connections || !Array.isArray(connections) || connections.length === 0) {
        connectionCache[targetUserId] = { status: 'none', timestamp: Date.now() };
        return 'none';
      }

      const connection = connections[0];
      let status: SimpleConnectionStatus;
      
      if (connection.status === 'matched') {
        status = 'matched';
      } else if (connection.status === 'sent') {
        if (connection.initiated_by === user.id) {
          status = 'sent';
        } else {
          status = 'received';
        }
      } else {
        status = 'none';
      }

      // Atualizar cache
      connectionCache[targetUserId] = { status, timestamp: Date.now() };
      return status;
    } catch (error) {
      debugLogger.error('Error checking connection status:', error);
      return 'none';
    }
  }, [user]);

  const likeProfile = useCallback(async (targetUserId: string): Promise<boolean> => {
    if (!user || loading) return false;

    // Check if user is blocked by target BEFORE attempting to like
    try {
      const { isBlockedBy } = await blockService.checkBlockStatus(user.id, targetUserId);
      if (isBlockedBy) {
        toast.error('Não é possível curtir este perfil');
        debugLogger.warn('SimpleConnectionStatus', 'Like blocked - user is blocked by target', {
          userId: user.id,
          targetUserId
        });
        return false;
      }
    } catch (blockError) {
      debugLogger.error('SimpleConnectionStatus', 'Error checking block status before like:', blockError);
      // Continue with like attempt, DB constraints will catch it if blocked
    }

    // Check daily like limits before proceeding
    if (!likeLimits.canLike) {
      const message = likeLimits.isUnlimited 
        ? 'Erro ao verificar limites de curtidas'
        : `Limite diário de curtidas atingido (${likeLimits.limit})`;
      
      toast.error(message);
      debugLogger.warn('SimpleConnectionStatus', 'Like blocked by daily limits', {
        current: likeLimits.current,
        limit: likeLimits.limit,
        canLike: likeLimits.canLike
      });
      return false;
    }
    
    setLoading(true);
    try {
      debugLogger.debug('SimpleConnectionStatus: Liking profile', targetUserId);
      
      const { error } = await supabase
        .from('connections')
        .insert({
          user_id: user.id,
          target_id: targetUserId,
          status: 'sent',
          initiated_by: user.id
        });

      if (error) {
        debugLogger.error('Error liking profile:', error);
        return false;
      }

      // Increment like count after successful like
      incrementLikeCount();

      // Invalidar cache para forçar refresh na próxima consulta
      delete connectionCache[targetUserId];
      return true;
    } catch (error) {
      debugLogger.error('Error liking profile:', error);
      return false;
    } finally {
      setLoading(false);
    }
  }, [user, loading, likeLimits, incrementLikeCount]);

  const unlikeProfile = useCallback(async (targetUserId: string): Promise<boolean> => {
    if (!user || loading) return false;
    
    setLoading(true);
    try {
      debugLogger.debug('SimpleConnectionStatus: Unliking profile', targetUserId);
      
      const { error } = await supabase
        .from('connections')
        .delete()
        .or(`and(user_id.eq.${user.id},target_id.eq.${targetUserId}),and(user_id.eq.${targetUserId},target_id.eq.${user.id})`);

      if (error) {
        debugLogger.error('Error unliking profile:', error);
        return false;
      }

      // Invalidar cache
      delete connectionCache[targetUserId];
      return true;
    } catch (error) {
      debugLogger.error('Error unliking profile:', error);
      return false;
    } finally {
      setLoading(false);
    }
  }, [user, loading]);

  const invalidateCache = useCallback((targetUserId?: string) => {
    if (targetUserId) {
      delete connectionCache[targetUserId];
    } else {
      Object.keys(connectionCache).forEach(key => delete connectionCache[key]);
    }
  }, []);

  return {
    getConnectionStatus,
    likeProfile,
    unlikeProfile,
    invalidateCache,
    loading,
    likeLimits
  };
};
